import { useRef } from 'react';
import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import { BarChart } from '@/components/analytics/charts/BarChart';
import { useChartDimensions } from '@/hooks/useChartDimensions';
import type { TimeEfficiencyData } from '@/types/analytics';
import styles from './TimeEfficiencyWidget.module.scss';

export interface TimeEfficiencyWidgetProps {
    data: TimeEfficiencyData[] | null;
    isLoading?: boolean;
    error?: Error | null;
}

/**
 * Time Efficiency Widget
 * 
 * Compares average time per question across difficulty levels.
 * Helps identify where user spends the most time.
 */
export function TimeEfficiencyWidget({ data, isLoading, error }: TimeEfficiencyWidgetProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { width } = useChartDimensions(containerRef as any);

    const isEmpty = !data || data.length === 0;

    // Prepare chart data
    const chartData = data
        ? data.map((d) => ({
            label: d.difficulty,
            value: d.avgTimeSeconds,
            color: 'var(--primary)',
        }))
        : [];

    return (
        <WidgetContainer
            title="Time Efficiency"
            description="Average time per question by difficulty"
            isLoading={isLoading}
            error={error}
            isEmpty={isEmpty}
            emptyMessage="Practice more to see time analysis"
            size="medium"
        >
            <div ref={containerRef} className={styles.container}>
                {width > 0 && (
                    <BarChart
                        data={chartData}
                        width={width}
                        height={200}
                        animate
                        showValues
                    />
                )}
            </div>
        </WidgetContainer>
    );
}
